// src/pages/SharedDossierView.jsx
// Phase 3 (§6/§17) — page PUBLIQUE d'un dossier partagé (route /dossier/:token).
// Lecture via edge `dossier-share` (valide durée/révocation + journalise l'accès).
// Réutilise les générateurs PDF pour le téléchargement. Aucune connexion requise.
import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase } from "../lib/supabaseClient";
import { generateSuccessionDossier } from "../utils/successionDossierPdf";
import { useTranslation } from "react-i18next";
import { generateInsuranceDossier } from "../utils/insuranceDossierPdf";
import { generateArtAssetDossier } from "../utils/artAssetDossierPdf";

const eur = (v) => (v || v === 0 ? `${Number(v).toLocaleString("fr-FR")} €` : "—");
const currentValue = (a) =>
  Number(a?.current_estimated_value ?? a?.valuation_estimated_value ?? a?.estimated_value ?? a?.value ?? 0) || 0;
const RECIPIENT = { insurer: "Assureur", notaire: "Notaire", bank: "Banque", expert: "Expert", family_office: "Family Office", other: "Destinataire" };

// Libellés des éléments ajoutés pour les partages « sans valeurs » (archives, galeries) : français, anglais, allemand.
const NEW_COPY = {
  fr: {
    print: "Imprimer", nature: "Nature de l'ensemble", status: "Statut", hidden: "Les valeurs et les prix ne sont pas communiqués dans ce dossier.",
    natures: { personal_collection: "Collection personnelle", company_collection: "Collection de société", gallery_archive: "Archives de galerie", artist_estate: "Estate d'artiste" },
    heritage: { gallery_stock: "Stock marchand de la galerie", personal: "Propriété personnelle", company: "Propriété de la société", deposit: "Dépôt", consignment: "Consignation", entrusted: "Œuvre confiée", to_clarify: "Statut à clarifier" },
  },
  en: {
    print: "Print", nature: "Type of collection", status: "Status", hidden: "Values and prices are not disclosed in this file.",
    natures: { personal_collection: "Personal collection", company_collection: "Company collection", gallery_archive: "Gallery archive", artist_estate: "Artist estate" },
    heritage: { gallery_stock: "Gallery commercial stock", personal: "Personal property", company: "Company property", deposit: "Deposit", consignment: "Consignment", entrusted: "Entrusted work", to_clarify: "Status to clarify" },
  },
  de: {
    print: "Drucken", nature: "Art des Bestands", status: "Status", hidden: "Werte und Preise werden in diesem Dossier nicht mitgeteilt.",
    natures: { personal_collection: "Persönliche Sammlung", company_collection: "Unternehmenssammlung", gallery_archive: "Galeriearchiv", artist_estate: "Künstlernachlass" },
    heritage: { gallery_stock: "Handelsbestand der Galerie", personal: "Privateigentum", company: "Eigentum der Gesellschaft", deposit: "Leihgabe", consignment: "Kommission", entrusted: "Anvertrautes Werk", to_clarify: "Status zu klären" },
  },
};

export default function SharedDossierView() {
  const { token } = useParams();
  const { i18n } = useTranslation();
  const pdfLang = String(i18n?.resolvedLanguage || i18n?.language || "fr").slice(0, 2);
  const [state, setState] = useState({ loading: true, error: "", data: null });

  useEffect(() => {
    let alive = true;
    (async () => {
      const { data, error } = await supabase.functions.invoke("dossier-share", { body: { token } });
      if (!alive) return;
      if (error) {
        // le corps d'erreur (410/403/404) est dans error.context si dispo
        let msg = "Lien invalide ou expiré.";
        try { const j = await error.context?.json?.(); if (j?.error) msg = j.error; } catch { /* noop */ }
        setState({ loading: false, error: msg, data: null });
      } else {
        setState({ loading: false, error: "", data });
      }
    })();
    return () => { alive = false; };
  }, [token]);

  const { loading, error, data } = state;
  const share = data?.share;
  const artworks = useMemo(() => data?.artworks || [], [data?.artworks]);
  const totalValue = useMemo(() => artworks.reduce((s, a) => s + currentValue(a), 0), [artworks]);
  const hideValues = share?.hide_values === true;
  const NC = NEW_COPY[pdfLang] || NEW_COPY.fr;

  const dl = {
    succession: () => generateSuccessionDossier({ collectionName: share?.collection_name || "", ownerName: share?.owner_name || "", artworks, benByArtwork: data?.beneficiaries || {}, lang: pdfLang }),
    insurance: () => generateInsuranceDossier({ collectionName: share?.collection_name || "", ownerName: share?.owner_name || "", artworks, insByArtwork: data?.insurance || {}, lang: pdfLang }),
    artAsset: () => artworks[0] && generateArtAssetDossier(artworks[0], { ownerName: share?.owner_name || "" }),
  };

  return (
    <div className="min-h-screen bg-[#faf9f7] px-4 py-10">
      <div className="mx-auto max-w-4xl">
        <div className="mb-6 flex items-center gap-2">
          <span className="inline-flex h-8 w-8 items-center justify-center rounded bg-black text-sm font-bold text-white">A</span>
          <span className="font-serif text-xl font-semibold" style={{ fontFamily: "Georgia, serif" }}>ARTKIV</span>
        </div>

        {loading && <div className="rounded-2xl border border-gray-200 bg-white p-8 text-center text-gray-500">Chargement du dossier…</div>}

        {!loading && error && (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center">
            <p className="text-lg font-medium text-rose-800">{error}</p>
            <p className="mt-1 text-sm text-rose-600">Contactez la personne qui vous a partagé ce dossier pour un nouveau lien.</p>
          </div>
        )}

        {!loading && !error && share && (
          <div className="space-y-6">
            <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
              <p className="text-xs font-semibold uppercase tracking-[0.14em] text-gray-400">Dossier patrimonial partagé</p>
              <h1 className="mt-1 font-serif text-2xl font-semibold text-gray-950" style={{ fontFamily: "Georgia, serif" }}>
                {share.collection_name || "Collection"}
              </h1>
              <div className="mt-3 grid grid-cols-2 gap-x-8 gap-y-2 text-sm sm:grid-cols-4">
                <div><div className="text-gray-400">Titulaire</div><div className="font-medium text-gray-900">{share.owner_name || "—"}</div></div>
                <div><div className="text-gray-400">Destinataire</div><div className="font-medium text-gray-900">{share.recipient_label || RECIPIENT[share.recipient_kind] || "—"}</div></div>
                <div><div className="text-gray-400">Œuvres</div><div className="font-medium text-gray-900">{artworks.length}</div></div>
                {hideValues ? (
                  <div><div className="text-gray-400">{NC.nature}</div><div className="font-medium text-gray-900">{NC.natures[share.collection_nature] || "—"}</div></div>
                ) : (
                  <div><div className="text-gray-400">Valeur totale</div><div className="font-medium text-gray-900">{eur(totalValue)}</div></div>
                )}
              </div>
              <p className="mt-3 text-xs text-gray-400">
                Accès valable jusqu'au {share.expires_at ? new Date(share.expires_at).toLocaleDateString("fr-FR") : "—"} · consultation journalisée.
              </p>

              <div className="mt-4 flex flex-wrap gap-2">
                {!hideValues && (share.dossier_type === "succession" || share.dossier_type === "full") && (
                  <button onClick={dl.succession} className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800">Dossier succession (PDF)</button>
                )}
                {!hideValues && (share.dossier_type === "insurance" || share.dossier_type === "full") && (
                  <button onClick={dl.insurance} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50">Cédule d'assurance (PDF)</button>
                )}
                {hideValues && (
                  <button onClick={() => window.print()} className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-800 hover:bg-gray-50">{NC.print}</button>
                )}
                {!hideValues && share.scope_type === "artwork" && (
                  <button onClick={dl.artAsset} className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800">Dossier de l'œuvre (PDF)</button>
                )}
              </div>
            </div>

            {/* Aperçu inventaire */}
            <div className="overflow-x-auto rounded-2xl border border-gray-200 bg-white p-2 shadow-sm">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wide text-gray-400">
                    <th className="p-3">Œuvre</th><th className="p-3">Artiste</th>{!hideValues && <th className="p-3 text-right">Valeur</th>}<th className="p-3">{hideValues ? NC.status : "Détention"}</th>
                  </tr>
                </thead>
                <tbody>
                  {artworks.slice().sort((a, b) => (hideValues ? String(a.title || "").localeCompare(String(b.title || "")) : currentValue(b) - currentValue(a))).map((a) => (
                    <tr key={a.id} className="border-b border-gray-100 last:border-0">
                      <td className="p-3 font-medium text-gray-900">{(a.is_exceptional ? "★ " : "") + (a.title || "(sans titre)")}</td>
                      <td className="p-3 text-gray-700">{a.artist || "—"}</td>
                      {!hideValues && <td className="p-3 text-right text-gray-700">{eur(currentValue(a))}</td>}
                      <td className="p-3 text-gray-600">{NC.heritage[a.heritage_status] || a.ownership_status || "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {hideValues && <p className="text-center text-xs text-gray-500">{NC.hidden}</p>}
            <p className="text-center text-xs text-gray-400">
              Dossier structuré via ARTKIV. Document informatif ; les décisions et validations relèvent des professionnels habilités.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
